import React from "react"
import {Prism as SyntaxHighlighter} from "react-syntax-highlighter"
import {dracula} from "react-syntax-highlighter/dist/cjs/styles/prism"

import globalStyles from "../../../../App.module.scss"
import {Arrow, Box, Diagram, DiagramCaption} from "../../Architecture/shared/Diagram"

const jenkinsfileExample = `pipeline {
    agent any
    stages {
        stage('Build') {
            steps {
                sh 'docker build -t harbor.internal/julcap/tools-frontend:\${GIT_COMMIT} .'
            }
        }
        stage('Push') {
            steps {
                sh 'docker push harbor.internal/julcap/tools-frontend:\${GIT_COMMIT}'
            }
        }
        stage('Deploy') {
            when { anyOf { branch 'develop'; branch 'staging'; branch 'main' } }
            steps {
                script {
                    def targets = [
                        develop: 'dev.tools.mydomain.com',
                        staging: 'staging.tools.mydomain.com',
                        main:    'tools.mydomain.com'
                    ]
                    sh "ssh deploy@\${targets[env.BRANCH_NAME]} 'docker compose pull && docker compose up -d'"
                }
            }
        }
    }
}`

export default function BehindThisSite() {
    return (
        <div className={globalStyles.article}>
            <h1>Behind This Site: Self-Hosted CI/CD on My Own Hardware</h1>

            <p>This whole site — the one you're using right now — runs on hardware I own, rebuilds and
                redeploys itself the moment I push to GitHub, and never touches a public cloud provider.
                I built the pipeline mostly to get real, repeatable practice at the kind of infra/DevOps
                work I do for clients: automated builds, a private registry, TLS, zero-touch deploys.
                Here's how it actually works.</p>

            <h3>The story</h3>
            <p>It started as a folder of personal scripts I kept re-copying between machines. Once that
                turned into a proper site — first for myself, later to share — I didn't want to keep
                dragging code onto a server by hand every time something changed. So instead of paying
                for a managed pipeline, I built the same kind of setup I'd stand up for a client: source
                control triggering automated builds, a private registry, and a scripted deploy — just
                running in a rack in my closet instead of somewhere in AWS.</p>

            <h3>The stack</h3>
            <div className={globalStyles.styledTable}>
                <table>
                    <thead>
                    <tr>
                        <th>Layer</th>
                        <th>Role</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><b>Proxmox VE</b></td>
                        <td>Hypervisor — every service below runs in its own VM on hardware I own and
                            maintain
                        </td>
                    </tr>
                    <tr>
                        <td><b>GitHub</b></td>
                        <td>Source control; a push fires a webhook that kicks off the pipeline</td>
                    </tr>
                    <tr>
                        <td><b>Jenkins</b></td>
                        <td>CI/CD orchestrator — builds, tests, and drives branch-based deploys</td>
                    </tr>
                    <tr>
                        <td><b>Docker</b></td>
                        <td>Packages the front-end and back-end into images</td>
                    </tr>
                    <tr>
                        <td><b>Harbor</b></td>
                        <td>Private container registry the built images are pushed to and pulled from</td>
                    </tr>
                    <tr>
                        <td><b>Reverse proxy</b></td>
                        <td>Terminates TLS and routes tools.mydomain.com to the running container</td>
                    </tr>
                    <tr>
                        <td><b>PostgreSQL</b></td>
                        <td>Application database, also self-hosted</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h3>Architecture</h3>
            <Diagram viewBox="0 0 680 280">
                <rect x={130} y={15} width={530} height={250} rx={10} fill="none"
                      stroke="var(--header-background)" strokeDasharray="4 4" opacity={0.4}/>
                <text x={395} y={30} textAnchor="middle" fontSize={12} opacity={0.8}>Proxmox — home lab
                </text>

                <Box x={10} y={50} width={90} height={34} label="GitHub" sublabel="git push"/>
                <Box x={150} y={50} width={110} height={34} label="Jenkins" sublabel="build & test" accent/>
                <Box x={300} y={50} width={110} height={34} label="Harbor" sublabel="image registry"/>

                <Box x={10} y={200} width={90} height={34} label="Users" sublabel="https"/>
                <Box x={150} y={200} width={110} height={34} label="Reverse proxy" sublabel="TLS termination"
                     accent/>
                <Box x={300} y={200} width={150} height={34} label="App container"
                     sublabel="tools.mydomain.com"/>

                <Arrow x1={100} y1={62} x2={150} y2={67} label="webhook"/>
                <Arrow x1={260} y1={67} x2={300} y2={67} label="push image"/>
                <Arrow x1={355} y1={84} x2={375} y2={200} label="pull image" dashed muted/>
                <Arrow x1={180} y1={84} x2={320} y2={200} label="deploy" dashed/>
                <Arrow x1={100} y1={217} x2={150} y2={217} label="https"/>
                <Arrow x1={260} y1={217} x2={300} y2={217} label="proxy"/>
            </Diagram>
            <DiagramCaption>GitHub push → Jenkins builds &amp; tests → image pushed to Harbor → Jenkins
                triggers a deploy → the target pulls the new image behind an SSL-terminating reverse
                proxy — all running on hardware I own.</DiagramCaption>

            <h3>The deploy pipeline, step by step</h3>
            <ol>
                <li>A push to GitHub fires a webhook to Jenkins.</li>
                <li>Jenkins checks out the branch, builds the front-end and back-end, and runs tests.</li>
                <li>On success, Jenkins builds a Docker image and pushes it to Harbor, my private
                    registry.
                </li>
                <li>Jenkins triggers the deploy step for that branch's target host.</li>
                <li>The target pulls the new image from Harbor and restarts the container.</li>
                <li>The reverse proxy, sitting in front of every service, terminates TLS and routes the
                    request to whichever container should handle it.
                </li>
            </ol>

            <h3>The Jenkinsfile</h3>
            <p>Deploys are branch-based — <code>develop</code>, <code>staging</code>, and <code>main</code>{' '}
                each promote to their own host. The target for each branch is hardcoded directly in the
                Jenkinsfile rather than pulled from an external config file: there's only one pipeline for
                this project, so a second source of truth for where things deploy would just be one more
                place to keep in sync for no real benefit. Simplified for illustration:</p>
            <SyntaxHighlighter
                children={jenkinsfileExample}
                style={dracula}
                PreTag="div"
                language={'groovy'}
            />

            <h3>Decisions I'd defend</h3>
            <ul>
                <li><b>Self-hosted Jenkins over a hosted CI service.</b> More to maintain, but full
                    control over the pipeline and no third-party build-minute quota — and it's the same
                    Jenkins I'd be asked to run for a client.
                </li>
                <li><b>Harbor over a public registry.</b> Private by default, with vulnerability scanning
                    on every image, for the cost of one more service to keep patched.
                </li>
                <li><b>Deploy config hardcoded in the Jenkinsfile, not an external config store.</b> One
                    file to read to understand the whole pipeline, no drift between code and config for a
                    single-project setup that doesn't need to be reused elsewhere.
                </li>
                <li><b>Branch-based promotion (develop → staging → main).</b> Changes prove themselves on
                    a throwaway environment before they reach the domain real visitors use.
                </li>
            </ul>

            <h3>What's next</h3>
            <p>The pipeline is solid; what it's missing is visibility — real monitoring and alerting
                instead of noticing an outage because I happened to check. That, and tightening the
                staging promotion so more regressions get caught before <code>main</code>.</p>
        </div>
    )
}
